/**
 * LetterPresenter — drives the UI builder's LetterPanel for the prologue's `letter` timeline steps.
 * Long beats are split into sentence-aligned chunks (the panel fits ~6 lines); after each chunk the
 * reader confirms (Enter / Z / tap / touch 确认; X / Esc completes typing and continues too).
 * Keeps the list of written beats so the panel can be closed while a memory stage runs and
 * re-filled instantly afterwards, or pre-filled on resume. Degrades to a console note without the UI.
 */
import Phaser from 'phaser';
import { Depth, FONT_BODY, GAME_W } from '@/core/config';
import { bus, Events } from '@/core/events';
import { getUi, uiRootOf } from '@/ui';
import { letterBeat, splitForDisplay, type LetterBeat } from '../letter';

interface PanelLike {
  isOpen(): boolean;
  open(): Promise<void>;
  addParagraph(text: string, opts?: { instant?: boolean }): Promise<void>;
  close(): Promise<void>;
  skip(): void;
}

export class LetterPresenter {
  private readonly written: LetterBeat[] = [];
  private waiting: (() => void) | null = null;
  private readonly hint: Phaser.GameObjects.Text;
  private alive = true;
  private readonly onConfirm = (): void => { if (this.acceptsInput()) this.resolveWait(); };
  private readonly onCancel = (): void => { if (!this.acceptsInput()) return; this.panel()?.skip(); this.resolveWait(); };
  private readonly onShutdown = (): void => this.destroy();

  constructor(private readonly scene: Phaser.Scene) {
    this.hint = scene.add.text(GAME_W / 2, 246, '▼ 继续', { fontFamily: FONT_BODY, fontSize: '11px', color: '#f1e3c2', stroke: '#0b0f14', strokeThickness: 3 }).setOrigin(0.5).setScrollFactor(0).setDepth(Depth.UI + 5).setVisible(false);
    bus.onTyped(Events.INPUT_CONFIRM, this.onConfirm);
    bus.onTyped(Events.INPUT_CANCEL, this.onCancel);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown);
  }

  get beats(): readonly LetterBeat[] { return this.written; }
  isWaiting(): boolean { return this.waiting !== null; }

  private panel(): PanelLike | null { return (getUi(this.scene)?.letterPanel as PanelLike | undefined) ?? null; }
  private acceptsInput(): boolean {
    if (!this.alive) return false;
    if (this.scene.scene.isPaused()) return false;
    return !uiRootOf(this.scene)?.hasModal();
  }

  /** Reveals one beat (chunk by chunk); resolves when the reader has confirmed the last chunk. */
  async reveal(beatId: string, opts: { instant: boolean }): Promise<void> {
    let beat: LetterBeat;
    try { beat = letterBeat(beatId); } catch (e) { console.warn('[prologue-letter]', e); return; }
    this.written.push(beat);
    const panel = this.panel();
    if (!panel) { console.warn(`[prologue-letter] no letter panel; beat "${beatId}" not shown`); return; }
    if (!panel.isOpen()) await panel.open();
    for (const chunk of splitForDisplay(beat.text)) {
      if (!this.alive) return;
      await panel.addParagraph(chunk, { instant: opts.instant });
      if (!opts.instant && this.alive) await this.waitConfirm();
    }
  }

  /** Writes beats instantly (resume / re-open after a memory stage). */
  async prefill(beats: readonly LetterBeat[]): Promise<void> {
    const panel = this.panel();
    for (const b of beats) {
      this.written.push(b);
      if (!panel || !this.alive) continue;
      if (!panel.isOpen()) await panel.open();
      for (const chunk of splitForDisplay(b.text)) await panel.addParagraph(chunk, { instant: true });
    }
  }

  async close(): Promise<void> {
    this.resolveWait();
    const panel = this.panel();
    if (panel?.isOpen()) await panel.close();
  }

  /** Re-fills the panel with everything written so far (after a memory stage). */
  async reopen(): Promise<void> {
    const beats = this.written.splice(0);
    await this.prefill(beats);
  }

  private waitConfirm(): Promise<void> {
    if (!this.alive) return Promise.resolve();
    this.hint.setVisible(true);
    return new Promise<void>((resolve) => { this.waiting = () => { this.hint.setVisible(false); resolve(); }; });
  }
  private resolveWait(): void {
    const w = this.waiting;
    this.waiting = null;
    w?.();
  }

  destroy(): void {
    if (!this.alive) return;
    this.alive = false;
    bus.offTyped(Events.INPUT_CONFIRM, this.onConfirm);
    bus.offTyped(Events.INPUT_CANCEL, this.onCancel);
    this.scene.events.off(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown);
    this.resolveWait();
    try { this.hint.destroy(); } catch { /* gone */ }
    const panel = this.panel();
    if (panel?.isOpen()) void panel.close();
  }
}
